/**
 * Hook for triggering patch generation on Constantine findings.
 * Calls POST /constantine/patch with selected risk keys.
 *
 * Tracks patch lifecycle states:
 * - Ready: no patch running, no results
 * - Running: patch job in progress (mutation pending or polling active)
 * - Patched: patch was generated successfully
 * - Patch Failed: patch generation failed
 */
import { ArrowPathIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useOsintContext } from '@/state/OsintContext';
import { ResourceType, Risk } from '@/types';

import { isConstantineFinding } from './useExploitVerification';

/** Patch lifecycle states for the button UI */
export type PatchState = 'ready' | 'running' | 'patched' | 'patch_failed';

/** Check if a risk already has patch results via tags */
export function hasPatchResults(risk: Risk): boolean {
  const tags = risk.tags ?? [];
  return tags.some(t => t === 'patched' || t === 'patch-attempted');
}

/** Check if a risk has a successful patch */
export function isPatched(risk: Risk): boolean {
  return (risk.tags ?? []).includes('patched');
}

/**
 * Determine the aggregate patch state for a set of risks.
 * - If any risk is patched, the aggregate is 'patched'
 * - If all have results but none patched, 'patch_failed'
 * - If none have results, 'ready'
 */
export function getPatchState(risks: Risk[]): PatchState {
  if (risks.length === 0) return 'ready';

  const allHaveResults = risks.every(hasPatchResults);

  if (risks.some(isPatched)) return 'patched';

  if (allHaveResults) return 'patch_failed';

  return 'ready';
}

/** Returns label, icon, className, and optional tooltip for the patch button based on current state */
export function getPatchButtonConfig(state: PatchState): {
  label: string;
  icon: ReactNode;
  className: string;
  tooltip?: string;
} {
  switch (state) {
    case 'running':
      return {
        label: 'Patching...',
        icon: <ArrowPathIcon className="size-4 motion-safe:animate-spin" />,
        className: 'bg-chariot-status-info text-white opacity-70 cursor-wait',
      };
    case 'patched':
      return {
        label: 'Patched',
        icon: <CheckCircleIcon className="size-4" />,
        className: 'bg-emerald-600 text-white',
      };
    case 'patch_failed':
      return {
        label: 'Patch Failed',
        icon: <XCircleIcon className="size-4" />,
        className: 'bg-chariot-status-low text-white',
        tooltip:
          'Patch generation produced no options — try a different finding or check CloudWatch logs',
      };
    case 'ready':
    default:
      return {
        label: 'Patch',
        icon: <DocumentTextIcon className="size-4" />,
        className: '',
      };
  }
}

export function usePatchGeneration(onComplete?: () => void) {
  const { isOsint } = useOsintContext();
  const axios = useAxios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, []);

  const isRunning = isSubmitting || isPolling;

  const handlePatch = useCallback(
    async (vulnerabilities: Risk[]): Promise<void> => {
      const constantineFindings = vulnerabilities.filter(isConstantineFinding);

      if (constantineFindings.length === 0) {
        toast.warning('No Constantine findings selected');
        return;
      }

      const alreadyPatched = constantineFindings.filter(hasPatchResults);
      if (alreadyPatched.length > 0) {
        const confirmed = window.confirm(
          `${alreadyPatched.length} of ${constantineFindings.length} finding(s) already have patches. Re-running will overwrite existing patch options. Continue?`
        );
        if (!confirmed) return;
      }

      const riskKeys = constantineFindings.map(r => r.key);
      const toastId = 'patch-generation';

      toast.loading(
        `Starting patch generation for ${riskKeys.length} finding${riskKeys.length > 1 ? 's' : ''}...`,
        { id: toastId }
      );

      setIsSubmitting(true);

      try {
        const patchUrl = isOsint
          ? '/constantine/patch?osint=true'
          : '/constantine/patch';
        await axios.post(patchUrl, {
          risk_keys: riskKeys,
        });

        toast.success(
          `Patch generation started for ${riskKeys.length} finding${riskKeys.length > 1 ? 's' : ''}`,
          { id: toastId, duration: 5000 }
        );

        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Job),
        });
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(''),
        });

        // Start polling for risk updates
        setIsPolling(true);

        // Clear any existing polling
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

        pollingRef.current = setInterval(() => {
          queryClient.invalidateQueries({
            queryKey: getQueryKey.getMy(ResourceType.Risk),
          });
        }, 5000);

        // Stop polling after 30 minutes
        pollingTimeoutRef.current = setTimeout(() => {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setIsPolling(false);
        }, 1800000);

        onComplete?.();
      } catch (error) {
        console.error('[usePatchGeneration] Failed:', error);
        toast.error('Failed to start patch generation', { id: toastId });
      } finally {
        setIsSubmitting(false);
      }
    },
    [axios, isOsint, onComplete]
  );

  /** Stop polling early (e.g., when results arrive via risk refresh) */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  return { handlePatch, isRunning, stopPolling };
}
